import {useState, useCallback, useEffect, useMemo} from 'react';

type CheckedState = boolean | 'indeterminate';

export interface TreeNode {
  id: string;
  name: string;
  label: string;
  parent?: TreeNode;
  children?: TreeNode[];
  isChecked: CheckedState;
}

// do updateNode
const updateNode = (node: TreeNode, checkState: CheckedState, doNotCheckChildren = false) => {
  // set node state
  node.isChecked = checkState;

  // check node's children recursively
  if (doNotCheckChildren === false && node.children && checkState != 'indeterminate') {
    node.children.forEach(n => updateNode(n, checkState));
  }

  // run up the tree to determine parent node's state
  if (node.parent) {
    updateNode(node.parent, getCheckStateFromChildrenState(node.parent.children || []), true);
  }
};

function getCheckStateFromChildrenState(nodes: TreeNode[]): CheckedState {
  if (nodes.length == 0) return false;
  const checkedCount = nodes.reduce((count, node) => {
    if (node.isChecked === true) return count + 1;
    else return count;
  }, 0);
  // const uncheckedCount = nodes.length - checkedCount;
  if (checkedCount == nodes.length) return true;
  if (checkedCount == 0) return false;
  return 'indeterminate';
}

function useTreeChecklist(props: {initialTree?: TreeNode[]; onChange?: (tree: TreeNode[]) => void}) {
  const {initialTree = [], onChange} = props;
  const [tree, setTree] = useState(initialTree);

  const setCheck = useCallback((node: TreeNode, checked?: boolean) => {
    setTree(prevTree => {
      const newTree = [...prevTree];
      // set (if true/false specified) or toggle if undefined
      const isChecked = checked === undefined ? !node.isChecked : checked;
      updateNode(node, isChecked);
      onChange?.(newTree);
      return newTree;
    });
  }, []);

  const toggleCheck = (node: TreeNode) => setCheck(node);

  const setCheckAll = useCallback(
    (checked: boolean) => {
      setTree(prevTree => {
        const newTree = [...prevTree];
        // set (if true/false specified) or toggle if undefined
        newTree.forEach(node => {
          updateNode(node, checked);
        });
        onChange?.(newTree);
        return newTree;
      });
    },
    [tree],
  );

  const allCheckState = useMemo(
    function (): CheckedState {
      return getCheckStateFromChildrenState(tree);
    },
    [tree],
  );

  const getAllCheckedNodes = useCallback(() => {
    const checkedNodes: TreeNode[] = [];
    const traverse = (node: TreeNode) => {
      if (node.isChecked) {
        checkedNodes.push(node);
      }
      if (node.children) {
        node.children.forEach(traverse);
      }
    };

    tree.forEach(traverse);
    return checkedNodes;
  }, [tree]);

  return {
    tree,
    setTree,
    toggleCheck,
    setCheck,
    isAllChecked: allCheckState,
    setCheckAll,
    updateNode, // without change notification    
    getAllCheckedNodes,
  };
}

export default useTreeChecklist;
